import Switch, { OptionType } from 'components/Switch'
import { usePoolSnapshots } from 'hooks/usePoolSnapshots'
import { PoolType } from 'pages/Liquidity/hooks/usePool'
import { useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import { ThemeContext } from 'styled-components'
import { ExternalLink } from 'theme/components'

import { ThemedText } from '../../../../theme'
import { useIslandContext } from '../hooks/useIslandContext'
import { FlexRowCenter } from '../styleds'
import AprView from './AprView'
import BaultView from './BaultView'
import HoldersDistributionView from './HoldersDistributionView'
import * as S from './styleds'
import TvlView from './TvlView'
import VolumeView from './VolumeView'

const ExternalAnalyticsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: ${({ theme }) => theme.cardSecondary};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  margin: 16px 0;
`

const ExternalLinkButton = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  text-decoration: underline dotted red;
  color: ${({ theme }) => theme.textSecondary};
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  margin-top: 20px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

type DirectionConfig = {
  showFooter: boolean
  showAssetSwitch?: boolean
  enableAssetSwitch?: boolean
  showTimeframeSwitch?: boolean
  enableTimeframeSwitch?: boolean
}

type Direction = 'tvl' | 'apr' | 'volume' | 'holders' | 'bault'

const DIRECTION_CONFIG: Record<Direction, DirectionConfig> = {
  tvl: {
    showFooter: true,
    showAssetSwitch: true,
    enableAssetSwitch: true,
    showTimeframeSwitch: true,
    enableTimeframeSwitch: true,
  },
  apr: {
    showFooter: true,
    showAssetSwitch: true,
    enableAssetSwitch: false,
    showTimeframeSwitch: true,
    enableTimeframeSwitch: true,
  },
  volume: {
    showFooter: true,
    showAssetSwitch: true,
    enableAssetSwitch: true,
    showTimeframeSwitch: true,
    enableTimeframeSwitch: true,
  },
  holders: {
    showFooter: false,
  },
  bault: {
    showFooter: true,
    showAssetSwitch: false,
    showTimeframeSwitch: true,
    enableTimeframeSwitch: true,
  },
}

const IslandAnalyticsCard = () => {
  const context = useIslandContext()

  const [direction, setDirection] = useState<Direction>('tvl')
  const [unit, setUnit] = useState<'both' | 'token0' | 'token1'>('both')
  const [timeframe, setTimeframe] = useState<'7' | '30' | '90' | 'max'>('90')
  const [period, setPeriod] = useState<'1' | '2'>('1')

  const theme = useContext(ThemeContext)

  const snapshotData = usePoolSnapshots(context.islandId, context.poolType as PoolType)
  const { snapshots } = snapshotData

  const isCharmPool = context.poolType === 'Charm'

  const ExternalAnalyticsView = () => {
    return (
      <ExternalAnalyticsContainer>
        <ExternalLink href={`https://app.charm.fi/berachain/vault/${context.islandId}`}>
          See on Charm&apos;s Website ↗
        </ExternalLink>
      </ExternalAnalyticsContainer>
    )
  }

  const directionOptions: OptionType<Direction>[] = useMemo(() => {
    const baseOptions: OptionType<Direction>[] = [
      { value: 'tvl', label: 'TVL' },
      { value: 'apr', label: 'APR' },
    ]

    baseOptions.push({ value: 'volume', label: 'Volume' }, { value: 'holders', label: 'Holders' })

    if (context.baultAddress) {
      baseOptions.push({ value: 'bault', label: 'Bault' })
    }

    return baseOptions
  }, [context.baultAddress])

  const unitOptions: OptionType<'both' | 'token0' | 'token1'>[] = [
    { value: 'both', label: 'Both' },
    { value: 'token0', label: context.currencyA.symbol },
    { value: 'token1', label: context.currencyB.symbol },
  ]

  const timeframeOptions: OptionType<'7' | '30' | '90' | 'max'>[] = [
    { value: '7', label: '7d' },
    { value: '30', label: '30d' },
    { value: '90', label: '90d' },
    { value: 'max', label: 'All time' },
  ]

  const showRewardVaultRewardTokenNotice = direction === 'apr' && timeframe === 'max' && Boolean(context.rewardVault)
  const rewardVaultRewardSymbol = context.rewardVaultRewardToken?.symbol

  const renderView = () => {
    if (isCharmPool && ['tvl', 'apr', 'volume'].includes(direction)) {
      return <ExternalAnalyticsView />
    }

    switch (direction) {
      case 'tvl':
        return <TvlView {...context} unit={unit} timeframe={timeframe} period={period} snapshots={snapshots} />
      case 'apr':
        return (
          <AprView
            {...context}
            timeframe={timeframe}
            snapshots={snapshots}
            rewardVaultRewardSymbol={rewardVaultRewardSymbol}
          />
        )
      case 'volume':
        return <VolumeView {...context} unit={unit} timeframe={timeframe} snapshots={snapshots} />
      case 'holders':
        return <HoldersDistributionView />
      case 'bault':
        return (
          <BaultView
            baultId={context.baultAddress}
            pool={context.poolApiResponse}
            timeframe={timeframe}
            rewardVaultRewardToken={context.rewardVaultRewardToken}
          />
        )
      default:
        return null
    }
  }

  const renderFooter = () => {
    const config = DIRECTION_CONFIG[direction]

    if (!config?.showFooter) return null

    if (isCharmPool && ['tvl', 'apr', 'volume'].includes(direction)) {
      return null
    }

    return (
      <S.IslandAnalyticsCardFooter>
        <FlexRowCenter>
          {config.showAssetSwitch && (
            <S.GraphModifierFlex>
              <ThemedText.DeprecatedBody fontSize="12px" fontWeight="400" lineHeight="20px" color={theme.textPrimary}>
                Asset
              </ThemedText.DeprecatedBody>
              <Switch
                options={unitOptions}
                activeValue={unit}
                setActiveValue={setUnit}
                disabled={!config.enableAssetSwitch}
              />
            </S.GraphModifierFlex>
          )}

          {config.showTimeframeSwitch && (
            <S.GraphModifierFlex>
              <ThemedText.DeprecatedBody fontSize="12px" fontWeight="400" lineHeight="20px" color={theme.textPrimary}>
                Timeframe
              </ThemedText.DeprecatedBody>
              <Switch
                options={timeframeOptions}
                activeValue={timeframe}
                setActiveValue={setTimeframe}
                disabled={!config.enableTimeframeSwitch}
              />
            </S.GraphModifierFlex>
          )}
        </FlexRowCenter>
      </S.IslandAnalyticsCardFooter>
    )
  }

  return (
    <S.IslandAnalyticsCard>
      <S.IslandAnalyticsCardHeader
        style={showRewardVaultRewardTokenNotice ? { height: 'auto', alignItems: 'flex-start' } : undefined}
      >
        {showRewardVaultRewardTokenNotice ? (
          <S.HeaderTitleGroup>
            <ThemedText.DeprecatedBody fontSize="14px" fontWeight="400" lineHeight="14px" color={theme.textPrimary}>
              Analytics
            </ThemedText.DeprecatedBody>
            <ThemedText.DeprecatedBody fontSize="11px" fontWeight="400" lineHeight="16px" color={theme.textSecondary}>
              {rewardVaultRewardSymbol || 'Reward'} APR is not available beyond 90 days.
            </ThemedText.DeprecatedBody>
          </S.HeaderTitleGroup>
        ) : (
          <ThemedText.DeprecatedBody fontSize="14px" fontWeight="400" lineHeight="14px" color={theme.textPrimary}>
            Analytics
          </ThemedText.DeprecatedBody>
        )}

        <S.GraphTypeSwitchWrapper>
          <Switch<Direction> options={directionOptions} activeValue={direction} setActiveValue={setDirection} />
        </S.GraphTypeSwitchWrapper>
      </S.IslandAnalyticsCardHeader>

      {renderView()}
      {renderFooter()}
    </S.IslandAnalyticsCard>
  )
}

export default IslandAnalyticsCard
